import React, { lazy, Suspense, ComponentType } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import { Home } from './pages';

type ComponentMap = {
  [key: string]: ComponentType;
};

// Explicitly import all components
const DnsLookup = lazy(() => import('./pages/DnsLookup'));
const TracerouteTester = lazy(() => import('./pages/TracerouteTester'));
const Ipv4PingTester = lazy(() => import('./pages/Ipv4PingTester'));
const Ipv6PingTester = lazy(() => import('./pages/Ipv6PingTester'));
const HttpHeaderViewer = lazy(() => import('./pages/HttpHeaderViewer'));
const JsonConverter = lazy(() => import('./pages/JsonConverter'));
const PhpConverter = lazy(() => import('./pages/PhpConverter'));
const PhpToJsonConverter = lazy(() => import('./pages/PhpToJsonConverter'));
const PortGenerator = lazy(() => import('./pages/PortGenerator'));
const Ipv6Generator = lazy(() => import('./pages/Ipv6Generator'));
const EmailGenerator = lazy(() => import('./pages/EmailGenerator'));
const MacGenerator = lazy(() => import('./pages/MacGenerator'));
const CidrCalculator = lazy(() => import('./pages/CidrCalculator'));
const Ipv6CidrCalculator = lazy(() => import('./pages/Ipv6CidrCalculator'));
const LineCounter = lazy(() => import('./pages/LineCounter'));
const EmailExtractor = lazy(() => import('./pages/EmailExtractor'));
const IpExtractor = lazy(() => import('./pages/IpExtractor'));
const RandomIpGenerator = lazy(() => import('./pages/RandomIpGenerator'));
const RandomProxyGenerator = lazy(() => import('./pages/RandomProxyGenerator'));
const TextPrefixSuffix = lazy(() => import('./pages/TextPrefixSuffix'));
const NewlineConverter = lazy(() => import('./pages/NewlineConverter'));
const UrlExtractor = lazy(() => import('./pages/UrlExtractor'));
const CaseConverter = lazy(() => import('./pages/string/CaseConverter'));
const StringCompare = lazy(() => import('./pages/string/StringCompare'));
const RegexTester = lazy(() => import('./pages/string/RegexTester'));
const Base64Encoder = lazy(() => import('./pages/encoding/Base64Encoder'));
const Base64Decoder = lazy(() => import('./pages/encoding/Base64Decoder'));
const JwtDecoder = lazy(() => import('./pages/jwt/JwtDecoder'));
const JwtGenerator = lazy(() => import('./pages/jwt/JwtGenerator'));
const PasswordGenerator = lazy(() => import('./pages/password/PasswordGenerator'));
const PasswordStrength = lazy(() => import('./pages/password/PasswordStrength'));
const UuidGenerator = lazy(() => import('./pages/uuid/UuidGenerator'));
const ColorGenerator = lazy(() => import('./pages/color/ColorGenerator'));
const QrGenerator = lazy(() => import('./pages/qr/QrGenerator'));
const MarkdownPreview = lazy(() => import('./pages/text/MarkdownPreview'));
const CsvToJson = lazy(() => import('./pages/data/CsvToJson'));
const JsonToCsv = lazy(() => import('./pages/data/JsonToCsv'));
const TextStatistics = lazy(() => import('./pages/text/TextStatistics'));
const TextDiff = lazy(() => import('./pages/text/TextDiff'));
const UnicodeExplorer = lazy(() => import('./pages/text/UnicodeExplorer'));
const TextEncoding = lazy(() => import('./pages/text/TextEncoding'));
const SearchReplace = lazy(() => import('./pages/text/SearchReplace'));
const CodeBeautifier = lazy(() => import('./pages/code/CodeBeautifier'));
const SvgToPng = lazy(() => import('./pages/code/SvgToPng'));
const ImageConverter = lazy(() => import('./pages/code/ImageConverter'));
const ImageColorExtractor = lazy(() => import('./pages/code/ImageColorExtractor'));
const CodeMinifier = lazy(() => import('./pages/code/CodeMinifier'));
const CronGenerator = lazy(() => import('./pages/code/CronGenerator'));
const TimestampConverter = lazy(() => import('./pages/time/TimestampConverter'));
const TimeZoneConverter = lazy(() => import('./pages/time/TimeZoneConverter'));
const DateCalculator = lazy(() => import('./pages/time/DateCalculator'));
const CountdownTimer = lazy(() => import('./pages/time/CountdownTimer'));
const StopwatchTimer = lazy(() => import('./pages/time/StopwatchTimer'));
const PomodoroTimer = lazy(() => import('./pages/time/PomodoroTimer'));
const WorldClock = lazy(() => import('./pages/time/WorldClock'));
const CalendarGenerator = lazy(() => import('./pages/time/CalendarGenerator'));
const DateFormatter = lazy(() => import('./pages/time/DateFormatter'));
const AgeCalculator = lazy(() => import('./pages/time/AgeCalculator'));
const BusinessDaysCalculator = lazy(() => import('./pages/time/BusinessDaysCalculator'));
const WeekNumberCalculator = lazy(() => import('./pages/time/WeekNumberCalculator'));
const SslChecker = lazy(() => import('./pages/network/SslChecker'));
const YamlToJson = lazy(() => import('./pages/data/YamlToJson'));
const JsonValidator = lazy(() => import('./pages/data/JsonValidator'));
const XmlValidator = lazy(() => import('./pages/data/XmlValidator'));
const CsvValidator = lazy(() => import('./pages/data/CsvValidator'));
const DataDiff = lazy(() => import('./pages/data/DataDiff'));
const DataTransformer = lazy(() => import('./pages/data/DataTransformer'));
const DataCleaner = lazy(() => import('./pages/data/DataCleaner'));
const DataMerger = lazy(() => import('./pages/data/DataMerger'));
const DataAggregator = lazy(() => import('./pages/data/DataAggregator'));
const DataFilter = lazy(() => import('./pages/data/DataFilter'));
const DataSorter = lazy(() => import('./pages/data/DataSorter'));
const DataGrouper = lazy(() => import('./pages/data/DataGrouper'));
const DataPivot = lazy(() => import('./pages/data/DataPivot'));
const DataSampling = lazy(() => import('./pages/data/DataSampling'));
const DataBinning = lazy(() => import('./pages/data/DataBinning'));
const DataOutliers = lazy(() => import('./pages/data/DataOutliers'));
const DataNormalization = lazy(() => import('./pages/data/DataNormalization'));
const DataRegression = lazy(() => import('./pages/data/DataRegression'));
const DataClustering = lazy(() => import('./pages/data/DataClustering'));
const DataDimensionality = lazy(() => import('./pages/data/DataDimensionality'));
const DataForecasting = lazy(() => import('./pages/data/DataForecasting'));
const DataAnomaly = lazy(() => import('./pages/data/DataAnomaly'));
const DataSegmentation = lazy(() => import('./pages/data/DataSegmentation'));
const DataSeasonality = lazy(() => import('./pages/data/DataSeasonality'));
const DataSmoothing = lazy(() => import('./pages/data/DataSmoothing'));
const DataInterpolation = lazy(() => import('./pages/data/DataInterpolation'));

// Hash components
const Md5Generator = lazy(() => import('./pages/hash/Md5Generator'));
const Sha1Generator = lazy(() => import('./pages/hash/Sha1Generator'));
const Sha224Generator = lazy(() => import('./pages/hash/Sha224Generator'));
const Sha256Generator = lazy(() => import('./pages/hash/Sha256Generator'));
const Sha384Generator = lazy(() => import('./pages/hash/Sha384Generator'));
const Sha512Generator = lazy(() => import('./pages/hash/Sha512Generator'));

// Create a map of routes to components
const componentMap: ComponentMap = {
  '/tools': Home,
  '/tools/dns': DnsLookup,
  '/tools/traceroute': TracerouteTester,
  '/tools/ping/ipv4': Ipv4PingTester,
  '/tools/ping/ipv6': Ipv6PingTester,
  '/tools/http-headers': HttpHeaderViewer,
  '/tools/json': JsonConverter,
  '/tools/json-to-php': PhpConverter,
  '/tools/php-to-json': PhpToJsonConverter,
  '/tools/port': PortGenerator,
  '/tools/ipv6': Ipv6Generator,
  '/tools/email': EmailGenerator,
  '/tools/mac': MacGenerator,
  '/tools/cidr': CidrCalculator,
  '/tools/ipv6-cidr': Ipv6CidrCalculator,
  '/tools/line-counter': LineCounter,
  '/tools/email-extractor': EmailExtractor,
  '/tools/ip-extractor': IpExtractor,
  '/tools/hash/md5': Md5Generator,
  '/tools/hash/sha1': Sha1Generator,
  '/tools/hash/sha224': Sha224Generator,
  '/tools/hash/sha256': Sha256Generator,
  '/tools/hash/sha384': Sha384Generator,
  '/tools/hash/sha512': Sha512Generator,
  '/tools/random-ip': RandomIpGenerator,
  '/tools/random-proxy': RandomProxyGenerator,
  '/tools/prefix-suffix': TextPrefixSuffix,
  '/tools/newline-converter': NewlineConverter,
  '/tools/url-extractor': UrlExtractor,
  '/tools/string/case': CaseConverter,
  '/tools/string/compare': StringCompare,
  '/tools/string/regex': RegexTester,
  '/tools/encoding/base64-encode': Base64Encoder,
  '/tools/encoding/base64-decode': Base64Decoder,
  '/tools/jwt/decode': JwtDecoder,
  '/tools/jwt/generate': JwtGenerator,
  '/tools/password/generate': PasswordGenerator,
  '/tools/password/strength': PasswordStrength,
  '/tools/uuid/generate': UuidGenerator,
  '/tools/color/generate': ColorGenerator,
  '/tools/qr/generate': QrGenerator,
  '/tools/text/markdown': MarkdownPreview,
  '/tools/data/csv-to-json': CsvToJson,
  '/tools/data/json-to-csv': JsonToCsv,
  '/tools/text/statistics': TextStatistics,
  '/tools/text/diff': TextDiff,
  '/tools/text/unicode': UnicodeExplorer,
  '/tools/text/encoding': TextEncoding,
  '/tools/text/search-replace': SearchReplace,
  '/tools/code/javascript-beautifier': CodeBeautifier,
  '/tools/code/svg-to-png': SvgToPng,
  '/tools/code/image-converter': ImageConverter,
  '/tools/code/image-colors': ImageColorExtractor,
  '/tools/code/minifier': CodeMinifier,
  '/tools/code/cron': CronGenerator,
  '/tools/time/timestamp': TimestampConverter,
  '/tools/time/timezone': TimeZoneConverter,
  '/tools/time/calculator': DateCalculator,
  '/tools/time/countdown': CountdownTimer,
  '/tools/time/stopwatch': StopwatchTimer,
  '/tools/time/pomodoro': PomodoroTimer,
  '/tools/time/world': WorldClock,
  '/tools/time/calendar': CalendarGenerator,
  '/tools/time/format': DateFormatter,
  '/tools/time/age': AgeCalculator,
  '/tools/time/business': BusinessDaysCalculator,
  '/tools/time/week': WeekNumberCalculator,
  '/tools/network/ssl-checker': SslChecker,
  '/tools/data/yaml-to-json': YamlToJson,
  '/tools/data/json-validator': JsonValidator,
  '/tools/data/xml-validator': XmlValidator,
  '/tools/data/csv-validator': CsvValidator,
  '/tools/data/diff': DataDiff,
  '/tools/data/transformer': DataTransformer,
  '/tools/data/cleaner': DataCleaner,
  '/tools/data/merger': DataMerger,
  '/tools/data/aggregator': DataAggregator,
  '/tools/data/filter': DataFilter,
  '/tools/data/sorter': DataSorter,
  '/tools/data/grouper': DataGrouper,
  '/tools/data/pivot': DataPivot,
  '/tools/data/sampling': DataSampling,
  '/tools/data/binning': DataBinning,
  '/tools/data/outliers': DataOutliers,
  '/tools/data/normalization': DataNormalization,
  '/tools/data/regression': DataRegression,
  '/tools/data/clustering': DataClustering,
  '/tools/data/dimensionality': DataDimensionality,
  '/tools/data/forecasting': DataForecasting,
  '/tools/data/anomaly': DataAnomaly,
  '/tools/data/segmentation': DataSegmentation,
  '/tools/data/seasonality': DataSeasonality,
  '/tools/data/smoothing': DataSmoothing,
  '/tools/data/interpolation': DataInterpolation
};

// Old path to new path mapping
const redirectMap: { [key: string]: string } = {
  // Text Processing Tools
  '/tools/count-lines-containing': '/tools/line-counter',
  '/tools/new-lines-to-spaces': '/tools/newline-converter',
  '/tools/prefix-suffix-lines': '/tools/prefix-suffix',

  // Network Tools
  '/tools/http-header-viewer': '/tools/http-headers',
  '/tools/random-ipv6-generator': '/tools/ipv6',
  '/tools/random-port-generator': '/tools/port',

  // Hash Generators
  '/tools/md2-hash-generator': '/tools/hash/md5',
  '/tools/md4-hash-generator': '/tools/hash/md5',
  '/tools/md5-hash-generator': '/tools/hash/md5',
  '/tools/sha1-hash-generator': '/tools/hash/sha1',
  '/tools/sha224-hash-generator': '/tools/hash/sha224',
  '/tools/sha384-hash-generator': '/tools/hash/sha384',
  '/tools/sha512-hash-generator': '/tools/hash/sha512'
};

function DynamicRouter() {
  const location = useLocation();

  // Check if this is an old path that needs redirection
  const newPath = redirectMap[location.pathname];
  if (newPath) {
    return <Navigate to={newPath} replace />;
  }

  // Get the component for the current path
  const Component = componentMap[location.pathname];
  
  // If no component exists for the path, redirect to /tools
  if (!Component) {
    console.warn(`No component found for path: ${location.pathname}`);
    return <Navigate to="/tools" replace />;
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Component />
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/tools" replace />} />
          <Route path="*" element={<DynamicRouter />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
